import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import UnprotectedRoute from './components/UnprotectedRoute';
import { TutorialProvider } from './context/TutorialContext';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import KnowledgeBase from './pages/KnowledgeBase';
import KnowledgeGenerator from './pages/KnowledgeGenerator';
import Transcripts from './pages/Transcripts';
import Settings from './pages/Settings';
import Users from './pages/Users';
import Sessions from './pages/Sessions';
import SessionDetail from './pages/SessionDetail';
import Subscription from './pages/Subscription';
import SupportTickets from './pages/SupportTickets';
import Calendar from './pages/Calendar';
import Training from './pages/Training';
import TrainingTracks from './pages/TrainingTracks';
import TrainingTrackBuilder from './pages/TrainingTrackBuilder';
import TrainingSessionEvaluation from './pages/TrainingSessionEvaluation';
import BulkUpload from './pages/BulkUpload';
import { store } from './store';
import { useRoleAccess } from './hooks/useRoleAccess';

/** Redirects to /knowledge-base for super admins, /dashboard for everyone else */
const DefaultRedirect: React.FC = () => {
  const { isSuperAdmin } = useRoleAccess();
  return <Navigate to={isSuperAdmin ? '/knowledge-base' : '/dashboard'} replace />;
};

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary>
          <Router>
            <TutorialProvider>
            <Routes>
              <Route path="/" element={<DefaultRedirect />} />
              <Route 
                path="/login" 
                element={
                  <UnprotectedRoute>
                    <Login />
                  </UnprotectedRoute>
                } 
              />
              <Route 
                path="/signup" 
                element={
                  <UnprotectedRoute>
                    <Signup />
                  </UnprotectedRoute>
                } 
              />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/knowledge-base"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <KnowledgeBase />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/knowledge-generator"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <KnowledgeGenerator />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/bulk-upload"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <BulkUpload />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transcripts" 
                element={
                  <ProtectedRoute>
                    <Transcripts />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/settings" 
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <Users />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="/sessions" 
                element={
                  <ProtectedRoute>
                    <Sessions />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <SessionDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <Subscription />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support-tickets"
                element={
                  <ProtectedRoute>
                    <SupportTickets />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/calendar"
                element={
                  <ProtectedRoute>
                    <Calendar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training"
                element={
                  <ProtectedRoute>
                    <Training />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/builder"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <TrainingTrackBuilder />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/builder/:trackId"
                element={
                  <ProtectedRoute>
                    <RoleProtectedRoute requiredRole="admin">
                      <TrainingTrackBuilder />
                    </RoleProtectedRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/training/sessions/:sessionId"
                element={
                  <ProtectedRoute>
                    <TrainingSessionEvaluation />
                  </ProtectedRoute>
                }
              />
              <Route path="/dictionary" element={<Navigate to="/knowledge-base" replace />} />
              <Route path="*" element={<DefaultRedirect />} />
            </Routes>
            </TutorialProvider>
          </Router>
        </ErrorBoundary>
      </ThemeProvider>
    </Provider>
  );
}

export default App;
